import { useAbilities } from '@aposphaere/core-kit'
import { EditIcon, EmailIcon } from '@aposphaere/ui-components'
import { format, isFuture, isSameDay } from 'date-fns'
import React, { useMemo } from 'react'
import { REMOTE_APPOINTMENT_TYPE_FRAGMENT } from '../../constants'
import { useModal, ModalKind } from '../../contexts/modalContext'

import { ModifiedAppointmentType } from './InfoContainerTabs'

type AppointmentType = Omit<ModifiedAppointmentType, 'date'> & { info: string; date: string; sent_confirmation_at: string | null  }

const ContentTemplate = ({ appointments, title }: { appointments: AppointmentType[]; title: string }) => {
  const abilities = useAbilities()
  const { openModal } = useModal()

  if (!appointments.length) {
    return null
  }

  return (
    <>
      <div className="mb-2 font-medium">{title}</div>
        {appointments.map(({ date, info, id, sent_confirmation_at }) => {
          const parseDate = (raw: string): Date | null => {
            const [d, m, yAndTime] = raw.split('.')
            if (!d || !m || !yAndTime) return null
            const [yyyy, time = '00:00'] = yAndTime.split(' ')
            const [hh, mm] = time.split(':')
            const parsed = new Date(
              Number(yyyy),
              Number(m) - 1,
              Number(d),
              Number(hh) || 0,
              Number(mm) || 0,
            )
            return isNaN(parsed.getTime()) ? null : parsed
          }

          const projectDate = typeof date === 'string' ? parseDate(date) : null
          const now = new Date()

          const isCurrentOrFutureMonth =
            !!projectDate &&
            (projectDate.getFullYear() > now.getFullYear() ||
              (projectDate.getFullYear() === now.getFullYear() &&
                projectDate.getMonth() >= now.getMonth()))

          return (
            <div className="flex items-center" key={id}>
              <span className="font-medium pr-3">{date}</span>
              <span className="flex items-center gap-1">
                {sent_confirmation_at && <EmailIcon dimension="25"/>}  
                {info}
              </span>
              <span>
                {abilities.edit_appointments && isCurrentOrFutureMonth && (
                  <button
                    className="-mt-1"
                    onClick={() =>
                      openModal({ kind: ModalKind.AppointmentEdit, id })
                    }
                  >
                    <EditIcon dimension="25" />
                  </button>
                )}
              </span>
            </div>
          )
        })}
    </>
  )
}

export const AppointmentsInfo = ({ appointments }: { appointments: ModifiedAppointmentType[] }) => {
  const { futureVisits, futureAppointments } = useMemo(() => {
    const futureVisits: AppointmentType[] = []
    const futureAppointments: AppointmentType[] = []
    console.log('appointments', appointments)
    appointments?.forEach((appointment) => {
      const relevantAppointment = appointment.date 

      if (!relevantAppointment) {
        return
      }

      const infoProjects = appointment.order_items
        .filter((item) => {
          const name = item.project?.name
          return !name || !name.startsWith('ApoCampus')
        })
        .map((item) => {
          const isRemote = appointment.appointmentType?.label?.toLowerCase() === REMOTE_APPOINTMENT_TYPE_FRAGMENT
          let name = item.project?.name
          if (isRemote) {
            return `${name}-Remote`
          }
          return name
        })
        .join(', ')

      const isCancelled = appointment.status?.id === '7'
      const isCompleted = appointment.status?.id === '3'

      let appointmentInfoResult

      if (isCancelled) {
        appointmentInfoResult = `Abgesagt${infoProjects ? ` (${infoProjects})` : ''}`
      } else if (isCompleted) {
        appointmentInfoResult = `Durchgeführt${infoProjects ? ` (${infoProjects})` : ''}`
      } else {
        appointmentInfoResult = `Avisiert${infoProjects ? ` (${infoProjects})` : ''}`
      }

      let formattedDate: string
      try {
        formattedDate = format(new Date(appointment.date!), 'dd.MM.yyyy HH:mm')
      } catch (err) {
        console.error('Invalid date:', appointment.date, err)
        formattedDate = 'Ungültiges Datum'
      }
      
      const newAppointment = {
        ...appointment,
        date: formattedDate,
        info: appointmentInfoResult,
        sent_confirmation_at: appointment.sent_confirmation_at ?? null,
      }
      

      if (appointment.isVisit) {
        futureVisits.push(newAppointment)
      } else {
        futureAppointments.push(newAppointment)
      }
    })

    return { futureVisits, futureAppointments }
  }, [appointments])

  return (
    <>
      {!futureVisits.length && !futureAppointments.length && <span>Keine Termine</span>}
      <ContentTemplate title="Termine:" appointments={futureAppointments} />
      <ContentTemplate title="Besuche:" appointments={futureVisits} />
    </>
  )
}
